"use client";

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Clock,
  Calendar,
  ArrowLeft,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import { useAuth } from "./context/AuthContext";
import { useToast } from "../hooks/use-toast";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Calendar as CalendarComponent } from "./ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { cn } from "../lib/utils";
import api from "../lib/api";
import { API } from "@/config/api.config";
import { NavbarDemo } from "./NavBar";

// Helper functions
const pad = (n) => n.toString().padStart(2, "0");
const toISODateOnly = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const timeToMinutes = (time) => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
};
const minutesToTime = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${pad(hours)}:${pad(mins)}`;
};

// Generate time slots based on therapist availability
const generateTimeSlots = (therapistAvailability, selectedDate) => {
  // If no availability data, show default 9am-9pm slots
  if (!therapistAvailability || !selectedDate) {
    const slots = [];
    for (let hour = 9; hour < 21; hour++) {
      slots.push(`${pad(hour)}:00`);
      slots.push(`${pad(hour)}:30`);
    }
    return slots;
  }

  const dayOfWeek = selectedDate.getDay();
  const dayNames = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];
  const dayKey = dayNames[dayOfWeek];
  const dayAvailability = therapistAvailability[dayKey];

  // Handle enhanced format (array of time ranges)
  if (Array.isArray(dayAvailability)) {
    if (dayAvailability.length === 0) {
      return [];
    }

    // Generate slots for all time ranges and merge them
    const allSlots = [];
    for (const range of dayAvailability) {
      if (range.startTime && range.endTime) {
        const startMinutes = timeToMinutes(range.startTime);
        const endMinutes = timeToMinutes(range.endTime);

        for (let minutes = startMinutes; minutes < endMinutes; minutes += 30) {
          const hours = Math.floor(minutes / 60);
          const mins = minutes % 60;
          allSlots.push(`${pad(hours)}:${pad(mins)}`);
        }
      }
    }

    // Remove duplicates and sort
    return [...new Set(allSlots)].sort();
  }

  // Handle legacy format (object with isWorking, startTime, endTime)
  if (
    !dayAvailability ||
    typeof dayAvailability === "number" ||
    !dayAvailability.isWorking
  ) {
    return [];
  }

  const startTime = dayAvailability.startTime || "09:00";
  const endTime = dayAvailability.endTime || "21:00";

  const slots = [];
  const startMinutes = timeToMinutes(startTime);
  const endMinutes = timeToMinutes(endTime);

  // Generate 30-minute slots between start and end time
  for (let minutes = startMinutes; minutes < endMinutes; minutes += 30) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    slots.push(`${pad(hours)}:${pad(mins)}`);
  }

  return slots;
};

// Calculate block position and height
const calculateBlockDimensions = (startTime, duration, timeSlots) => {
  const startMinutes = timeToMinutes(startTime);

  // Find the closest slot index for start time
  let startSlotIndex = timeSlots.findIndex((slot) => {
    const slotMinutes = timeToMinutes(slot);
    return slotMinutes >= startMinutes;
  });

  // If no exact match, find the closest slot
  if (startSlotIndex === -1) {
    // Find closest slot by comparing differences
    let minDiff = Infinity;
    timeSlots.forEach((slot, idx) => {
      const slotMinutes = timeToMinutes(slot);
      const diff = Math.abs(slotMinutes - startMinutes);
      if (diff < minDiff) {
        minDiff = diff;
        startSlotIndex = idx;
      }
    });
  }

  if (startSlotIndex === -1) return null;

  const endMinutes = startMinutes + duration;

  // Find the closest slot index for end time
  let endSlotIndex = timeSlots.findIndex((slot) => {
    const slotMinutes = timeToMinutes(slot);
    return slotMinutes >= endMinutes;
  });

  // If no exact match, find the closest slot
  if (endSlotIndex === -1) {
    let minDiff = Infinity;
    timeSlots.forEach((slot, idx) => {
      const slotMinutes = timeToMinutes(slot);
      const diff = Math.abs(slotMinutes - endMinutes);
      if (diff < minDiff) {
        minDiff = diff;
        endSlotIndex = idx;
      }
    });
    // If still not found, use the last slot
    if (endSlotIndex === -1) {
      endSlotIndex = timeSlots.length;
    }
  }

  const slotHeight = 30; // 30px per 30-minute slot

  // Calculate exact pixel position based on minutes
  const firstSlotMinutes = timeToMinutes(
    timeSlots[startSlotIndex] || timeSlots[0],
  );
  const offsetFromSlot = startMinutes - firstSlotMinutes;
  const top = startSlotIndex * slotHeight + (offsetFromSlot / 30) * slotHeight;

  // Calculate height based on duration
  const height = (duration / 30) * slotHeight;

  return { top, height, startSlotIndex };
};

export default function ReschedulePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();

  // Get appointment data from navigation state
  const appointmentData = location.state?.appointment;

  // Helper to check if date is today or in the past
  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < today;
  };

  // Initialize selectedDate with appointment date
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const appointment = location.state?.appointment;
    if (appointment?.startsAt) {
      const appointmentDate = new Date(appointment.startsAt);
      appointmentDate.setHours(0, 0, 0, 0);
      // Always show the appointment's date (even if in past, but we prevent navigation to past)
      // This ensures the calendar loads showing where the appointment is
      return appointmentDate;
    }
    return today;
  });

  // Ensure selectedDate is set correctly when appointmentData changes (on initial load)
  useEffect(() => {
    if (appointmentData?.startsAt) {
      const appointmentDate = new Date(appointmentData.startsAt);
      appointmentDate.setHours(0, 0, 0, 0);

      // Set the selected date to appointment date on initial load
      // This ensures the calendar view shows the appointment date first
      setSelectedDate((prevDate) => {
        const prev = new Date(prevDate);
        prev.setHours(0, 0, 0, 0);
        // Only update if different to avoid unnecessary re-renders
        if (appointmentDate.getTime() !== prev.getTime()) {
          return appointmentDate;
        }
        return prevDate;
      });
    }
  }, [appointmentData?.startsAt]);

  const [appointments, setAppointments] = useState([]);
  const [aways, setAways] = useState([]);
  const [availabilities, setAvailabilities] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [editDate, setEditDate] = useState(null);
  const [editTime, setEditTime] = useState("");
  const [editEndTime, setEditEndTime] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [operatingHours, setOperatingHours] = useState(null);
  const [therapistAvailability, setTherapistAvailability] = useState(null);

  // Generate time slots based on therapist availability (only working hours)
  // If no availability but there are appointments, generate default slots to show them
  const timeSlots = useMemo(() => {
    const slots = generateTimeSlots(therapistAvailability, selectedDate);
    
    // If no slots but there are appointments for this date, generate default slots
    // to ensure appointments are visible
    if (slots.length === 0 && appointments.length > 0) {
      const dateStr = toISODateOnly(selectedDate);
      const hasAppointmentsOnDate = appointments.some((apt) => {
        const aptDate = new Date(apt.appointmentDate);
        const aptDateStr = toISODateOnly(aptDate);
        return aptDateStr === dateStr;
      });
      
      if (hasAppointmentsOnDate) {
        // Generate default 9am-9pm slots to show appointments
        const defaultSlots = [];
        for (let hour = 9; hour < 21; hour++) {
          defaultSlots.push(`${pad(hour)}:00`);
          defaultSlots.push(`${pad(hour)}:30`);
        }
        return defaultSlots;
      }
    }
    
    return slots;
  }, [therapistAvailability, selectedDate, appointments]);

  // Get the date string for the selected date
  const selectedDateStr = useMemo(
    () => toISODateOnly(selectedDate),
    [selectedDate],
  );

  // Fetch all data for the selected date
  const fetchDayData = useCallback(async () => {
    if (!appointmentData?.therapistId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const dateStr = toISODateOnly(selectedDate);

      // Fetch appointments for the therapist on this date
      const therapistAppointments =
        await api.appointments.fetchTherapistAppointments(
          appointmentData.therapistId,
          dateStr,
        );

      // Transform appointments to include proper structure
      const transformedAppointments = (therapistAppointments || []).map(
        (apt) => ({
          _id: apt._id || apt.id,
          appointmentDate: apt.appointmentDate || apt.startsAt,
          duration: apt.duration || apt.durationMins || 60,
          clientName: apt.clientName || apt.clientEmail || "Client",
          status: apt.status || "pending",
          notes: apt.notes || "",
        }),
      );

      // Get all appointments (user's + others)
      setAppointments(transformedAppointments);

      // Fetch aways
      const therapistAways = await api.appointments.fetchTherapistAways(
        appointmentData.therapistId,
      );
      setAways(therapistAways || []);

      // Fetch availability
      const availabilityData =
        await api.appointments.fetchTherapistAvailability(
          appointmentData.therapistId,
          new Date(selectedDate).toISOString(),
          new Date(selectedDate).toISOString(),
        );
      // availabilityData is an object with date keys, each containing an array of slots
      const slotsForDate = availabilityData[dateStr] || [];
      // Slots can be strings or objects with time property
      setAvailableSlots(slotsForDate);

      // Fetch clinic operating hours
      try {
        const clinicId =
          localStorage.getItem("clinic_id") ||
          (typeof process !== "undefined" &&
            (process.env?.NEXT_PUBLIC_DEFAULT_CLINIC_ID ||
              process.env?.REACT_APP_DEFAULT_CLINIC_ID)) ||
          "";

        if (clinicId) {
          const token = localStorage.getItem("bookatherapy_token");
          const res = await fetch(`${API}/clinic/${clinicId}`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            credentials: "include",
          });
          if (res.ok) {
            const clinicData = await res.json();
            if (clinicData?.operatingHours) {
              setOperatingHours(clinicData.operatingHours);
            }
          }
        }
      } catch (error) {
        console.warn("Failed to fetch clinic operating hours:", error);
      }

      // Fetch therapist availability schedule
      try {
        const token = localStorage.getItem("bookatherapy_token");
        const res = await fetch(
          `${API}/availability/${appointmentData.therapistId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            credentials: "include",
          },
        );
        if (res.ok) {
          const availabilityData = await res.json();
          if (availabilityData?.availability) {
            setTherapistAvailability(availabilityData.availability);
          }
        }
      } catch (error) {
        console.warn("Failed to fetch therapist availability:", error);
      }
    } catch (error) {
      console.error("Failed to fetch day data:", error);
      toast({
        title: "Error",
        description: "Failed to load schedule data",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [appointmentData, selectedDate, toast]);

  useEffect(() => {
    fetchDayData();
  }, [fetchDayData]);

  // Check if a time slot is available
  const isSlotAvailable = useCallback(
    (timeSlot) => {
      // availableSlots can be an array of strings or objects with time property
      return availableSlots.some((slot) => {
        if (typeof slot === "string") {
          return slot === timeSlot;
        }
        return slot.time === timeSlot || slot === timeSlot;
      });
    },
    [availableSlots],
  );

  // Check if therapist is available at a given time slot
  const isTherapistAvailable = useCallback(
    (timeSlot) => {
      if (!therapistAvailability) {
        return false;
      }

      const dayOfWeek = selectedDate.getDay();
      const dayNames = [
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
      ];
      const dayKey = dayNames[dayOfWeek];
      const dayAvailability = therapistAvailability[dayKey];

      if (!dayAvailability || typeof dayAvailability === "number") {
        return false;
      }

      // Handle enhanced format (array of time ranges)
      if (Array.isArray(dayAvailability)) {
        return dayAvailability.some(
          (range) =>
            range.startTime &&
            range.endTime &&
            timeSlot >= range.startTime &&
            timeSlot < range.endTime
        );
      }

      // Handle legacy format (object with isWorking)
      if (!dayAvailability.isWorking) {
        return false;
      }

      return (
        timeSlot >= dayAvailability.startTime &&
        timeSlot < dayAvailability.endTime
      );
    },
    [therapistAvailability, selectedDate],
  );

  // Check if a time slot is in an away period
  const isInAwayPeriod = useCallback(
    (timeSlot) => {
      const dateStr = toISODateOnly(selectedDate);
      return aways.some((away) => {
        const awayFrom = new Date(away.from);
        const awayTo = new Date(away.to);
        const awayFromDate = awayFrom.toISOString().slice(0, 10);
        const awayToDate = awayTo.toISOString().slice(0, 10);

        if (dateStr < awayFromDate || dateStr > awayToDate) {
          return false;
        }

        const startTime = away.startTimeSlot || away.timeSlot || "00:00";
        const duration = away.duration || 60;
        const endTime = minutesToTime(timeToMinutes(startTime) + duration);

        return timeSlot >= startTime && timeSlot < endTime;
      });
    },
    [aways, selectedDate],
  );

  // Get appointments for a specific time slot
  const getAppointmentsAtTime = useCallback(
    (timeSlot) => {
      return appointments.filter((apt) => {
        const aptDate = new Date(apt.appointmentDate);
        const aptTime = `${pad(aptDate.getHours())}:${pad(
          aptDate.getMinutes(),
        )}`;
        return aptTime === timeSlot;
      });
    },
    [appointments],
  );

  // Check if a time slot is occupied (excluding the current appointment being edited)
  const isTimeSlotOccupied = useCallback(
    (timeSlot, date) => {
      if (!date) return false;
      const dateStr = toISODateOnly(date);

      // Check if this time slot conflicts with any existing appointment
      // (excluding the appointment being edited)
      return appointments.some((apt) => {
        // Skip the appointment being edited
        if (selectedAppointment && apt._id === selectedAppointment._id) {
          return false;
        }

        const aptDate = new Date(apt.appointmentDate);
        const aptDateStr = toISODateOnly(aptDate);

        // Only check appointments on the same date
        if (aptDateStr !== dateStr) {
          return false;
        }

        const aptStartTime = `${pad(aptDate.getHours())}:${pad(
          aptDate.getMinutes(),
        )}`;
        const aptDuration = apt.duration || 60;
        const aptEndTime = minutesToTime(
          timeToMinutes(aptStartTime) + aptDuration,
        );

        // Check if the time slot falls within this appointment's time range
        return timeSlot >= aptStartTime && timeSlot < aptEndTime;
      });
    },
    [appointments, selectedAppointment],
  );

  // Handle clicking on an appointment block
  const handleAppointmentClick = (appointment) => {
    // Only allow editing the user's own appointment
    if (isUserAppointment(appointment)) {
      setSelectedAppointment(appointment);
      const aptDate = new Date(appointment.appointmentDate);
      setEditDate(aptDate);

      const startTime = `${pad(aptDate.getHours())}:${pad(
        aptDate.getMinutes(),
      )}`;
      const duration = appointment.duration || 60;
      const endTime = minutesToTime(timeToMinutes(startTime) + duration);

      setEditTime(startTime);
      setEditEndTime(endTime);
      setEditModalOpen(true);
    } else {
      // Show info toast for other appointments
      toast({
        title: "Other Appointment",
        description: "You can only edit your own appointments",
      });
    }
  };

  // Handle saving the edited appointment
  const handleSaveAppointment = async () => {
    if (!selectedAppointment || !editDate || !editTime || !editEndTime) {
      return;
    }

    setIsSaving(true);
    try {
      const [hours, minutes] = editTime.split(":").map(Number);
      const newDateTime = new Date(editDate);
      newDateTime.setHours(hours, minutes, 0, 0);

      // Calculate duration from fromTime and toTime
      const startMinutes = timeToMinutes(editTime);
      const endMinutes = timeToMinutes(editEndTime);
      const durationMins = endMinutes - startMinutes;

      const clientName = user?.fullName || user?.email || "Client";

      const payload = {
        clientName: clientName,
        therapistId: appointmentData.therapistId,
        appointmentDate: newDateTime.toISOString(),
        duration: durationMins,
        notes: appointmentData.notes || "",
        status: appointmentData.status || "pending",
      };

      const appointmentId =
        selectedAppointment._id || selectedAppointment.id || appointmentData.id;

      // Store the old date before updating
      const oldDateStr = selectedDateStr;
      const newDateStr = toISODateOnly(editDate);

      await api.appointments.updateAppointment(appointmentId, payload);

      toast({
        title: "Appointment rescheduled",
        description: `Successfully rescheduled to ${newDateTime.toLocaleDateString()} at ${newDateTime.toLocaleTimeString()}`,
      });

      // Close modal first so user can see the update
      setEditModalOpen(false);
      setSelectedAppointment(null);
      setEditTime("");
      setEditEndTime("");

      // Small delay to ensure backend has processed the update
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Update selected date if it changed
      if (newDateStr !== oldDateStr) {
        // Clear appointments and update date
        // useEffect will automatically trigger fetchDayData when selectedDate changes
        setAppointments([]);
        setSelectedDate(editDate);
      } else {
        // If same date, refresh immediately to show updated appointment
        // Clear appointments first to force re-render
        setAppointments([]);
        // Fetch fresh data for the current date
        await fetchDayData();
      }
    } catch (error) {
      console.error("Failed to update appointment:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to reschedule appointment",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Navigate to previous/next day (prevent going to past dates)
  const navigateDay = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);

    // Prevent navigating to past dates
    if (isPastDate(newDate)) {
      return;
    }

    setSelectedDate(newDate);
  };

  // Check if current date is today
  const isToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const currentDate = new Date(selectedDate);
    currentDate.setHours(0, 0, 0, 0);
    return currentDate.getTime() === today.getTime();
  };

  // Format date display
  const formatDateDisplay = (date) => {
    return new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(date);
  };

  // Get appointment block dimensions
  const getAppointmentBlock = (appointment) => {
    const aptDate = new Date(appointment.appointmentDate);
    const startTime = `${pad(aptDate.getHours())}:${pad(aptDate.getMinutes())}`;
    const duration = appointment.duration || 60;
    return calculateBlockDimensions(startTime, duration, timeSlots);
  };

  // Check if appointment is user's appointment
  const isUserAppointment = (appointment) => {
    const aptId = appointment._id || appointment.id;
    const targetId = appointmentData?.id || appointmentData?._id;
    return aptId === targetId || aptId?.toString() === targetId?.toString();
  };

  // Check if therapist is working on a given date
  const isTherapistWorkingOnDate = useCallback(
    (date) => {
      if (!therapistAvailability || !date) {
        return false;
      }

      const dayOfWeek = date.getDay();
      const dayNames = [
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
      ];
      const dayKey = dayNames[dayOfWeek];
      const dayAvailability = therapistAvailability[dayKey];

      if (!dayAvailability || typeof dayAvailability === "number") {
        return false;
      }

      // Handle enhanced format (array of time ranges)
      if (Array.isArray(dayAvailability)) {
        return dayAvailability.length > 0;
      }

      // Handle legacy format (object with isWorking)
      return dayAvailability.isWorking === true;
    },
    [therapistAvailability],
  );

  // Check if a specific time slot is available (not past, not away, not occupied)
  const isTimeSlotAvailable = useCallback(
    (time, date) => {
      if (!time || !date || !therapistAvailability) {
        return false;
      }

      const dateStr = toISODateOnly(date);
      const [hours, minutes] = time.split(":").map(Number);
      const testDateTime = new Date(date);
      testDateTime.setHours(hours, minutes, 0, 0);

      // Check if time is in the past
      const now = new Date();
      if (testDateTime < now) {
        return false;
      }

      // Check if in away period
      const isAway = aways.some((away) => {
        const awayFrom = new Date(away.from);
        const awayTo = new Date(away.to);
        const awayFromDate = awayFrom.toISOString().slice(0, 10);
        const awayToDate = awayTo.toISOString().slice(0, 10);

        if (dateStr < awayFromDate || dateStr > awayToDate) {
          return false;
        }

        const startTime = away.startTimeSlot || away.timeSlot || "00:00";
        const duration = away.duration || 60;
        const endTime = minutesToTime(timeToMinutes(startTime) + duration);

        return time >= startTime && time < endTime;
      });

      if (isAway) {
        return false;
      }

      // Check if occupied (excluding current appointment)
      const isOccupied = isTimeSlotOccupied(time, date);

      return !isOccupied;
    },
    [aways, isTimeSlotOccupied, therapistAvailability],
  );

  // Check if selected times are available
  const areSelectedTimesAvailable = useMemo(() => {
    if (!editDate || !editTime || !editEndTime) {
      return false;
    }

    // Check if start time is available
    if (!isTimeSlotAvailable(editTime, editDate)) {
      return false;
    }

    // Check if end time is available
    if (!isTimeSlotAvailable(editEndTime, editDate)) {
      return false;
    }

    // Check if any time in the range is occupied
    const startMinutes = timeToMinutes(editTime);
    const endMinutes = timeToMinutes(editEndTime);

    for (
      let checkMinutes = startMinutes;
      checkMinutes < endMinutes;
      checkMinutes += 30
    ) {
      const checkTime = minutesToTime(checkMinutes);
      if (!isTimeSlotAvailable(checkTime, editDate)) {
        return false;
      }
    }

    return true;
  }, [editDate, editTime, editEndTime, isTimeSlotAvailable]);

  // Check if there are any available time slots for the edit date
  const hasAvailableTimeSlots = useMemo(() => {
    if (!editDate || !therapistAvailability) {
      return false;
    }

    // Check if therapist is working on this date
    if (!isTherapistWorkingOnDate(editDate)) {
      return false;
    }

    // Generate time slots for the edit date
    const editDateSlots = generateTimeSlots(therapistAvailability, editDate);
    if (editDateSlots.length === 0) {
      return false;
    }

    const dateStr = toISODateOnly(editDate);
    const now = new Date();

    // Check if there's at least one available slot
    return editDateSlots.some((time) => {
      const [hours, minutes] = time.split(":").map(Number);
      const testDateTime = new Date(editDate);
      testDateTime.setHours(hours, minutes, 0, 0);

      // Check if time is in the past
      if (testDateTime < now) {
        return false;
      }

      // Check if in away period
      const isAway = aways.some((away) => {
        const awayFrom = new Date(away.from);
        const awayTo = new Date(away.to);
        const awayFromDate = awayFrom.toISOString().slice(0, 10);
        const awayToDate = awayTo.toISOString().slice(0, 10);

        if (dateStr < awayFromDate || dateStr > awayToDate) {
          return false;
        }

        const startTime = away.startTimeSlot || away.timeSlot || "00:00";
        const duration = away.duration || 60;
        const endTime = minutesToTime(timeToMinutes(startTime) + duration);

        return time >= startTime && time < endTime;
      });

      if (isAway) {
        return false;
      }

      // Check if occupied (excluding current appointment)
      const isOccupied = isTimeSlotOccupied(time, editDate);

      // This slot is available if not occupied
      return !isOccupied;
    });
  }, [
    editDate,
    therapistAvailability,
    aways,
    isTherapistWorkingOnDate,
    isTimeSlotOccupied,
    appointments,
    selectedAppointment,
  ]);

  if (!appointmentData) {
    return (
      <main className="min-h-screen bg-gradient-to-b from-palette-cloud via-white to-white">
        <NavbarDemo />
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-palette-phantom mb-4">
              No appointment selected
            </h1>
            <Button onClick={() => navigate("/dashboard")}>
              Go to Dashboard
            </Button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gradient-to-b from-palette-cloud via-white to-white">
      <NavbarDemo />

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate("/dashboard")}
              className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1 className="text-3xl font-bold text-palette-phantom">
              Reschedule Appointment
            </h1>
            <p className="text-palette-space mt-2">
              Rescheduling appointment with {appointmentData.therapist}
            </p>
            <p className="text-sm text-palette-space mt-1">
              Click on your appointment (highlighted in yellow) to edit the date
              and time
            </p>
          </div>
        </div>

        {/* Date Navigation */}
        <div className="mb-6 flex items-center justify-between bg-white rounded-lg p-4 shadow-sm border border-palette-smoke/50">
          <Button
            variant="outline"
            onClick={() => navigateDay(-1)}
            disabled={isToday()}
            className="rounded-lg disabled:opacity-50 disabled:cursor-not-allowed">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-4">
            <Calendar className="h-5 w-5 text-palette-navy" />
            <span className="text-lg font-semibold text-palette-phantom">
              {formatDateDisplay(selectedDate)}
            </span>
            <Button
              variant="outline"
              onClick={() => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                setSelectedDate(today);
              }}
              disabled={isToday()}
              className="text-sm disabled:opacity-50 disabled:cursor-not-allowed">
              Today
            </Button>
          </div>
          <Button
            variant="outline"
            onClick={() => navigateDay(1)}
            className="rounded-lg">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Calendar View */}
        <div className="bg-white rounded-lg shadow-lg border border-palette-smoke/50 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center p-12">
              <p className="text-palette-space">Loading schedule...</p>
            </div>
          ) : (
            <div className="flex">
              {/* Time Column */}
              <div className="sticky left-0 z-30 bg-white border-r border-palette-smoke/50 min-w-[80px] flex-shrink-0">
                <div className="h-12 border-b border-palette-smoke/50 flex items-center justify-center bg-palette-cloud/30">
                  <Clock className="h-4 w-4 text-palette-phantom" />
                </div>
                {timeSlots.map((time) => (
                  <div
                    key={time}
                    className="h-[30px] border-b border-palette-smoke/30 flex items-center justify-center text-xs text-palette-space">
                    {time}
                  </div>
                ))}
              </div>

              {/* Schedule Column */}
              <div
                className="flex-1 relative"
                style={{
                  minHeight: `${48 + Math.max(timeSlots.length * 30, 400)}px`,
                }}>
                {timeSlots.length === 0 ? (
                  <div className="flex items-center justify-center h-full p-12">
                    <div className="text-center">
                      <p className="text-palette-phantom font-semibold mb-2">
                        Therapist not working on this day
                      </p>
                      <p className="text-sm text-palette-space">
                        Please select a different date
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Header row to match time column */}
                    <div className="h-12 border-b border-palette-smoke/50 flex items-center justify-center bg-palette-cloud/30">
                      <span className="text-xs font-medium text-palette-phantom">
                        {formatDateDisplay(selectedDate)}
                      </span>
                    </div>

                    {/* Available slots background */}
                    {timeSlots.map((time) => {
                      const isAvailable = isSlotAvailable(time);
                      const isAway = isInAwayPeriod(time);
                      const slotAppointments = getAppointmentsAtTime(time);

                      return (
                        <div
                          key={time}
                          className={cn(
                            "h-[30px] border-b border-palette-smoke/30 relative",
                            // Only show away periods (gray) and available slots (green)
                            // Non-working hours are excluded from timeSlots, so no red needed
                            isAway && "bg-gray-200/60", // Gray for away periods
                            isAvailable && !isAway && "bg-green-50/30", // Green for available slots
                          )}
                        />
                      );
                    })}

                    {/* Away periods */}
                    {aways
                      .filter((away) => {
                        const awayFrom = new Date(away.from);
                        const awayTo = new Date(away.to);
                        const awayFromDate = awayFrom
                          .toISOString()
                          .slice(0, 10);
                        const awayToDate = awayTo.toISOString().slice(0, 10);
                        return (
                          selectedDateStr >= awayFromDate &&
                          selectedDateStr <= awayToDate
                        );
                      })
                      .map((away, idx) => {
                        const startTime =
                          away.startTimeSlot || away.timeSlot || "00:00";
                        const duration = away.duration || 60;
                        const dimensions = calculateBlockDimensions(
                          startTime,
                          duration,
                          timeSlots,
                        );
                        if (!dimensions) return null;

                        return (
                          <div
                            key={`away-${idx}`}
                            className="absolute left-0 right-0 bg-gray-300/70 border border-gray-400 rounded px-2 py-1 z-10"
                            style={{
                              top: `${48 + dimensions.top}px`, // Offset by header height (48px)
                              height: `${dimensions.height}px`,
                            }}>
                            <div className="text-xs text-gray-700 font-medium">
                              Away: {startTime} ({duration}min)
                            </div>
                          </div>
                        );
                      })}

                    {/* Appointment blocks */}
                    {appointments.map((appointment) => {
                      const block = getAppointmentBlock(appointment);
                      if (!block) return null;

                      const isUser = isUserAppointment(appointment);
                      const aptDate = new Date(appointment.appointmentDate);
                      const startTime = `${pad(aptDate.getHours())}:${pad(
                        aptDate.getMinutes(),
                      )}`;
                      const endTime = minutesToTime(
                        timeToMinutes(startTime) + (appointment.duration || 60),
                      );

                      return (
                        <div
                          key={appointment._id}
                          onClick={() => handleAppointmentClick(appointment)}
                          className={cn(
                            "absolute left-1 right-1 rounded-lg border-2 px-2 py-1 cursor-pointer transition-all z-20 shadow-sm",
                            isUser
                              ? "bg-yellow-100 border-yellow-400 hover:bg-yellow-200 hover:ring-2 hover:ring-yellow-500 hover:shadow-md"
                              : "bg-blue-100 border-blue-300 hover:bg-blue-200 opacity-75",
                          )}
                          style={{
                            top: `${48 + block.top}px`, // Offset by header height (48px)
                            height: `${Math.max(block.height, 60)}px`,
                          }}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-semibold text-gray-900 flex items-center gap-1">
                                {isUser && (
                                  <span className="inline-block w-2 h-2 bg-yellow-600 rounded-full" />
                                )}
                                {isUser
                                  ? "Your Appointment"
                                  : appointment.clientName || "Appointment"}
                              </div>
                              <div className="text-[10px] text-gray-600 mt-0.5">
                                {startTime} - {endTime}
                              </div>
                              <div className="text-[10px] text-gray-500 mt-0.5">
                                {appointment.duration || 60} min
                              </div>
                            </div>
                            {isUser && (
                              <Badge className="text-[9px] h-4 px-1.5 bg-yellow-600 text-white border-0">
                                Edit
                              </Badge>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="mt-4 flex flex-wrap gap-4 text-sm text-palette-space">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-yellow-100 border border-yellow-300 rounded" />
            <span>Your Appointment</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-blue-100 border border-blue-300 rounded" />
            <span>Other Appointments</span>
          </div>
          {/* <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-50 border border-green-200 rounded" />
            <span>Available Slots</span>
          </div> */}
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gray-300 border border-gray-400 rounded" />
            <span>Away Periods</span>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Appointment</DialogTitle>
            <DialogDescription>
              Change the date and time for your appointment
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Date Picker */}
            <div>
              <label className="text-sm font-medium text-palette-phantom mb-2 block">
                Date
              </label>
              <CalendarComponent
                mode="single"
                selected={editDate}
                onSelect={setEditDate}
                defaultMonth={editDate || new Date()}
                disabled={(date) => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);

                  // Disable past dates
                  if (date < today) {
                    return true;
                  }

                  // Disable dates when therapist is not working
                  if (!isTherapistWorkingOnDate(date)) {
                    return true;
                  }

                  return false;
                }}
                className="rounded-md border "
                classNames={{
                  months:
                    "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                  month: "space-y-4",
                  caption: "flex justify-center pt-1 relative items-center",
                  caption_label: "text-sm font-medium",
                  nav: "space-x-1 flex items-center",
                  nav_button:
                    "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
                  nav_button_previous: "absolute left-1",
                  nav_button_next: "absolute right-1",
                  table: "w-full border-collapse space-y-1",
                  head_row: "flex",
                  head_cell: " rounded-md w-8 font-normal text-[0.8rem]",
                  row: "flex w-full mt-2",
                  cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20",
                  day: "h-8 w-8 p-0 font-normal  aria-selected:opacity-100",
                  day_selected: "bg-yellow-400 text-white rounded-md",
                  day_today: "bg-black rounded-md text-white font-semibold",
                  day_outside: "text-gray-600 opacity-80",
                  day_disabled: "text-gray-600 opacity-50 cursor-not-allowed",
                  day_range_middle:
                    "aria-selected:bg-gray-700 aria-selected:text-gray-200",
                  day_hidden: "invisible",
                }}
              />
            </div>

            {/* From Time Selector */}
            <div>
              <label className="text-sm font-medium text-palette-phantom mb-2 block">
                From Time
              </label>
              <Select
                value={editTime}
                onValueChange={(newTime) => {
                  setEditTime(newTime);
                  // Reset end time if it's before or equal to the new start time
                  if (
                    editEndTime &&
                    timeToMinutes(editEndTime) <= timeToMinutes(newTime)
                  ) {
                    setEditEndTime("");
                  }
                }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select start time" />
                </SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  {(() => {
                    // Generate time slots for the edit date (only working hours)
                    const editDateSlots = editDate
                      ? generateTimeSlots(therapistAvailability, editDate)
                      : [];

                    return editDateSlots.map((time) => {
                      if (!editDate) return null;
                      const dateStr = toISODateOnly(editDate);
                      const [hours, minutes] = time.split(":").map(Number);
                      const testDateTime = new Date(editDate);
                      testDateTime.setHours(hours, minutes, 0, 0);

                      // Check if in away period
                      const isAway = aways.some((away) => {
                        const awayFrom = new Date(away.from);
                        const awayTo = new Date(away.to);
                        const awayFromDate = awayFrom
                          .toISOString()
                          .slice(0, 10);
                        const awayToDate = awayTo.toISOString().slice(0, 10);

                        if (dateStr < awayFromDate || dateStr > awayToDate) {
                          return false;
                        }

                        const startTime =
                          away.startTimeSlot || away.timeSlot || "00:00";
                        const duration = away.duration || 60;
                        const endTime = minutesToTime(
                          timeToMinutes(startTime) + duration,
                        );

                        return time >= startTime && time < endTime;
                      });

                      // Check if time is in the past
                      const now = new Date();
                      const isPast = testDateTime < now;

                      // Check if occupied (excluding current appointment)
                      const isOccupied = isTimeSlotOccupied(time, editDate);

                      // Disable if past, away, or occupied
                      const isDisabled = isPast || isAway || isOccupied;

                      return (
                        <SelectItem
                          key={time}
                          value={time}
                          disabled={isDisabled}>
                          {new Date(`2000-01-01T${time}`).toLocaleTimeString(
                            "en-US",
                            {
                              hour: "numeric",
                              minute: "2-digit",
                            },
                          )}
                          {isOccupied && " (Occupied)"}
                          {isAway && " (Away)"}
                          {isPast && " (Past)"}
                        </SelectItem>
                      );
                    });
                  })()}
                </SelectContent>
              </Select>
            </div>

            {/* To Time Selector */}
            <div>
              <label className="text-sm font-medium text-palette-phantom mb-2 block">
                To Time
              </label>
              <Select
                value={editEndTime}
                onValueChange={setEditEndTime}
                disabled={!editTime}>
                <SelectTrigger>
                  <SelectValue placeholder="Select end time" />
                </SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  {(() => {
                    if (!editTime || !editDate) return null;

                    // Generate time slots for the edit date (only working hours)
                    const editDateSlots = generateTimeSlots(
                      therapistAvailability,
                      editDate,
                    );

                    const startMinutes = timeToMinutes(editTime);

                    return editDateSlots
                      .filter((time) => {
                        // Only show times after the selected start time
                        const timeMinutes = timeToMinutes(time);
                        return timeMinutes > startMinutes;
                      })
                      .map((time) => {
                        const dateStr = toISODateOnly(editDate);
                        const [hours, minutes] = time.split(":").map(Number);
                        const testDateTime = new Date(editDate);
                        testDateTime.setHours(hours, minutes, 0, 0);

                        // Check if in away period
                        const isAway = aways.some((away) => {
                          const awayFrom = new Date(away.from);
                          const awayTo = new Date(away.to);
                          const awayFromDate = awayFrom
                            .toISOString()
                            .slice(0, 10);
                          const awayToDate = awayTo.toISOString().slice(0, 10);

                          if (dateStr < awayFromDate || dateStr > awayToDate) {
                            return false;
                          }

                          const startTime =
                            away.startTimeSlot || away.timeSlot || "00:00";
                          const duration = away.duration || 60;
                          const endTime = minutesToTime(
                            timeToMinutes(startTime) + duration,
                          );

                          return time >= startTime && time < endTime;
                        });

                        // Check if time is in the past
                        const now = new Date();
                        const isPast = testDateTime < now;

                        // Check if any part of the time range (from editTime to this time) is occupied
                        const timeMinutes = timeToMinutes(time);
                        let isOccupied = false;
                        for (
                          let checkMinutes = startMinutes;
                          checkMinutes < timeMinutes;
                          checkMinutes += 30
                        ) {
                          const checkTime = minutesToTime(checkMinutes);
                          if (isTimeSlotOccupied(checkTime, editDate)) {
                            isOccupied = true;
                            break;
                          }
                        }

                        // Disable if past, away, or occupied
                        const isDisabled = isPast || isAway || isOccupied;

                        return (
                          <SelectItem
                            key={time}
                            value={time}
                            disabled={isDisabled}>
                            {new Date(`2000-01-01T${time}`).toLocaleTimeString(
                              "en-US",
                              {
                                hour: "numeric",
                                minute: "2-digit",
                              },
                            )}
                            {isOccupied && " (Occupied)"}
                            {isAway && " (Away)"}
                            {isPast && " (Past)"}
                          </SelectItem>
                        );
                      });
                  })()}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditModalOpen(false);
                setSelectedAppointment(null);
              }}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveAppointment}
              disabled={
                isSaving ||
                !editDate ||
                !editTime ||
                !editEndTime ||
                !hasAvailableTimeSlots ||
                !areSelectedTimesAvailable
              }>
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
            {!hasAvailableTimeSlots && editDate && (
              <p className="text-sm text-red-600 mt-2">
                No available time slots for this date. Please select a different
                date.
              </p>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
